import sha256 from 'js-sha256';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, applyVerticalStyles, pageContext, interpolateString, apiAction } from 'ctm-util-functions';
import { excludePageFromJourney } from 'ctm-util-functions/lib/excludePages';
import { radioButtonGroupUIComponent } from 'ctm-css-themes';
import { getBrand } from 'ctm-app/client/store/selectors';

import classNames from 'classnames';
import { quoteFormSelector } from 'ctm-selectors';
import styles from './loading_page.css';

import { LoadingSpinner } from '../../loading_animation/loading_spinner';
import { setFormValue } from '../actions';

const DEFAULT_LOAD_TIME_MS = 5000; // Default minimum load time of 5 seconds if maxLoadTime is not specified in schema

const roundToNearestHundred = (value) => {
    // Avoid Number() coercing null/''/whitespace-only strings to 0, which would mask a missing average as a real $0 estimate
    const numericValue = typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')
        ? Number(value)
        : NaN;

    return Number.isFinite(numericValue)
        ? Math.round(numericValue / 100) * 100
        : value;
};

const normalizeApiResponse = (apiIdentifier, response) => {
    if (apiIdentifier !== 'contentsCostCalculator'
        || !response
        || typeof response !== 'object'
        || Array.isArray(response)
        || response.average === undefined) {
        return response;
    }

    return {
        ...response,
        average: roundToNearestHundred(response.average)
    };
};

/**
 * FormattedInput component with increment/decrement buttons
 * Allows users to adjust numeric values using +/- buttons
 */
class _LoadingPage extends Component {

    constructor(props) {
        super(props);
        this.styles = applyVerticalStyles(radioButtonGroupUIComponent, 'radio_button');
        this.state = {
            messageIndex: 0,
            isFading: false
        };
    }

    componentDidMount() {
        const { dispatch, schema, formData, journeyId, brandCode, serverConfig } = this.props;
        this.isMounted_ = true;
        // If the loading page is reached via a browser back/forward navigation,
        // skip the loading page entirely by going back one more step.
        // This acts as a safety net alongside replaceHistory:true on goToNextPage —
        // if the replace fires after a back action (race condition), the loading page
        // can still end up in history and this guard catches that case.
        const browserHistory = pageContext.getProps().browserHistory;
        if (browserHistory.action === 'POP') {
            browserHistory.go(-1);
            return;
        }

        dispatch({
            type: 'TRACK_LOADING_PAGE',
            label: pageContext.getProps().pageState.pageName
        });

        const apis = get(schema, 'apis');
        // isAnonPrefill is used below to skip existing-value retention and to clear stale
        // values on API failure when resuming from an anonymous-prefill session.
        const isAnonPrefill = get(formData, 'helpers.prefill.attempt') > 0;
        const activeApis = apis
            ? Object.values(apis).filter((apiData) => {
                const ruleResultPath = ['rules', get(apiData, 'onLoadRule'), 'result'].join('.');
                return get(apiData, 'api.onLoad') && get(this.props, ruleResultPath);
            })
            : [];

        const { visualProps = {} } = get(schema, 'field', {});
        const { maxLoadTime, messages = [] } = visualProps;
        const minWait = maxLoadTime ? maxLoadTime * 1000 : DEFAULT_LOAD_TIME_MS;

        // Shared guard — ensures goToNextPage is called at most once regardless of
        // whether the minWait timer or the last API response fires first.
        let hasNavigated = false;
        const navigateOnce = () => {
            if (!hasNavigated) {
                hasNavigated = true;
                this.goToNextPage();
            }
        };

        // Navigate as soon as minWait expires, even if APIs are still in-flight
        this.nextPageDelayTimer = setTimeout(() => {
            this.nextPageDelayTimer = null;
            navigateOnce();
        }, minWait);

        // Evaluate and snapshot message conditions at mount time — API responses can
        // mutate the state that compound rule conditions depend on (e.g. tracking properties
        // like gnafIdMatch), which would cause render() to re-evaluate to an empty result
        // mid-display. Snapshotting here ensures the messages stay stable for the full duration.
        this.activeMessagesSnapshot = this.getActiveMessages();

        // Swap to the second message halfway through the timer duration
        if (this.activeMessagesSnapshot.length > 1) {
            this.messageTimer = setTimeout(() => {
                this.handleMessageTransition();
            }, minWait / 2);
        }

        if (activeApis.length > 0) {
            // Dispatch all active API calls simultaneously (parallel).
            // Navigation is always driven by nextPageDelayTimer — no need to track API completion for that purpose.
            const pageState = pageContext.getProps().pageState;
            activeApis.forEach((apiData) => {
                const apiSchema = apiData.api.onLoad;
                apiSchema.queryStringValues = [{ varPath: 'brand_code', value: brandCode }];

                // Clear any existing API response in form state before making the call
                dispatch(
                    setFormValue(
                        [apiData.apiIdentifier, 'apiOnLoadResponse'].join('.'),
                        undefined,
                        schema.stateKey,
                        true
                    )
                );
                dispatch(
                    setFormValue(
                        [apiData.apiIdentifier, 'apiOnLoadError'].join('.'),
                        undefined,
                        schema.stateKey,
                        true
                    )
                );

                // Capture existing customer-selected values before clearing, so they can be retained if already populated.
                // This applies to both normal and anon prefill flows — user selections in the current session
                // should always be preserved, even when calling the API again during prefill verification.
                const existingPrefillValues = {};
                if (apiData.apiDependentFields) {
                    apiData.apiDependentFields.forEach((field) => {
                        if (field.prefillIdentifier) {
                            const existingValue = get(formData, field.prefillIdentifier);
                            const normalizedExistingValue = typeof existingValue === 'string'
                                ? existingValue.trim()
                                : existingValue;
                            const hasExistingSelection = normalizedExistingValue !== undefined
                                && normalizedExistingValue !== null
                                && normalizedExistingValue !== ''
                                && normalizedExistingValue !== 0
                                && normalizedExistingValue !== '0';
                            if (hasExistingSelection) {
                                existingPrefillValues[field.prefillIdentifier] = existingValue;
                            }
                        }
                    });
                }

                const payload = {};
                if (apiSchema.requestData) {
                    apiSchema.requestData.forEach((property) => {
                        // Special handling for journeyId as it is not part of formData but is required for API calls to tie them to the user journey
                        if (property.key === 'journeyId') {
                            payload[property.key] = journeyId;
                            return;
                        }
                        // Build the payload based on the specified form data paths in the schema
                        const value = get(formData, property.value);
                        if (value !== undefined) {
                            payload[property.key] = value;
                        }
                    });
                }

                const action = apiAction(apiSchema, pageState, [], apiSchema.baseUrlName, payload);
                if (apiSchema.customHeaders) {
                    const payloadRequestData = { ...payload };
                    delete payloadRequestData.journeyId;
                    const sortedPayload = Object.keys(payloadRequestData).sort().reduce((acc, key) => {
                        acc[key] = payloadRequestData[key];
                        return acc;
                    }, {});
                    const partnerHash = sha256(JSON.stringify(sortedPayload));
                    action.requestHeaders = { 'x-correlation-id': journeyId, 'x-partner-hash': partnerHash };
                }

                action.responseFunc = (response) => {
                    if (!this.isMounted_) return;

                    const normalizedResponse = normalizeApiResponse(apiData.apiIdentifier, response);

                    dispatch(
                        setFormValue(
                            [apiData.apiIdentifier, 'apiOnLoadResponse'].join('.'),
                            normalizedResponse,
                            schema.stateKey,
                            true
                        )
                    );

                    // Populate dependent fields based on the API response
                    if (apiData.apiDependentFields) {
                        const isRebuildVariant = apiData.apiIdentifier === 'rebuildCostCalculator'
                            && serverConfig.get('rebuildCostCalculatorEnabled');
                        const isContentsVariant = apiData.apiIdentifier === 'contentsCostCalculator'
                            && serverConfig.get('contentsCostCalculatorEnabled');
                        const isPrefillVariant = isRebuildVariant || isContentsVariant;
                        apiData.apiDependentFields.forEach((field) => {
                            // If rangeFromApi is specified, validate that the response range falls within the CTM range before populating the field
                            if (field.rangeFromApi) {
                                const minKey = get(field, 'rangeFromApi.apiMin');
                                const maxKey = get(field, 'rangeFromApi.apiMax');
                                const min = minKey ? get(normalizedResponse, minKey) : undefined;
                                const max = maxKey ? get(normalizedResponse, maxKey) : undefined;
                                const ctmMinThreshold = serverConfig && serverConfig.get('homeRebuildMinThreshold');
                                const ctmMaxThreshold = serverConfig && serverConfig.get('homeRebuildMaxThreshold');
                                if (!(min && max && ctmMinThreshold && ctmMaxThreshold)
                                    || min < ctmMinThreshold || max > ctmMaxThreshold) {
                                    // API range is outside acceptable bounds, skipping population of field
                                    return;
                                }
                            }
                            const prefillValueKey = get(field, 'prefillValueFromApi');
                            const prefillValue = prefillValueKey ? get(normalizedResponse, prefillValueKey) : undefined;
                            // Priority: 1) retain existing customer selection, 2) use API value if prefill variant, 3) leave empty
                            const valueToUse = existingPrefillValues[field.prefillIdentifier] || (isPrefillVariant ? prefillValue : undefined);
                            if (field.prefillIdentifier && valueToUse) {
                                dispatch(
                                    setFormValue(
                                        field.prefillIdentifier,
                                        valueToUse.toString(),
                                        schema.stateKey,
                                        true
                                    )
                                );
                            }
                        });
                    }

                    if (apiData.trackingProperties) {
                        apiData.trackingProperties.forEach((property) => {
                            dispatch(setFormValue(property.identifier, get(formData, property.value), schema.stateKey, true));
                        });
                    }

                    this.trackApiResult(apiData.apiIdentifier, 'success');
                };

                action.errorFunc = (error) => {
                    if (!this.isMounted_) return;
                    dispatch(setFormValue([apiData.apiIdentifier, 'apiOnLoadError'].join('.'), error, schema.stateKey, true));

                    // On rebuild API failures, clear dependent field values so cover_details does not
                    // show a stale or zero-like rebuild cost.
                    // Only clear zero-like values OR values that were NOT entered by the user in the current session.
                    // User-entered values (tracked in existingPrefillValues) should be preserved on error.
                    if (apiData.apiIdentifier === 'rebuildCostCalculator' && apiData.apiDependentFields) {
                        apiData.apiDependentFields.forEach((field) => {
                            if (!field.prefillIdentifier) return;
                            const currentValue = get(formData, field.prefillIdentifier);
                            const normalizedValue = typeof currentValue === 'string'
                                ? currentValue.trim()
                                : currentValue;
                            const isEmptyOrZero = normalizedValue === undefined
                                || normalizedValue === null
                                || normalizedValue === ''
                                || normalizedValue === 0
                                || normalizedValue === '0';

                            // Only clear if empty/zero, or if it's an anon prefill stale value (not user-entered)
                            const isUserEntered = field.prefillIdentifier in existingPrefillValues;
                            if (isEmptyOrZero || (isAnonPrefill && !isUserEntered)) {
                                dispatch(
                                    setFormValue(
                                        field.prefillIdentifier,
                                        undefined,
                                        schema.stateKey,
                                        true
                                    )
                                );
                            }
                        });
                    }

                    this.trackApiResult(apiData.apiIdentifier, 'fail');
                };

                dispatch(action);
            });
        } else {
            this.goToNextPage();
        }
    }

    trackApiResult(apiIdentifier, result) {
        const trackedApis = {
            rebuildCostCalculator: 'rebuild',
            contentsCostCalculator: 'contents'
        };
        const api = trackedApis[apiIdentifier];

        if (api) {
            const { dispatch } = this.props;
            dispatch({
                type: 'TRACK_LOADING_PAGE_API_RESULT',
                api,
                result
            });
        }
    }

    getActiveMessages() {
        const { schema } = this.props;
        const { visualProps = {} } = get(schema, 'field', {});
        const { messages = [] } = visualProps;

        if (!messages.length) return [];

        // New format: array of { condition, content } objects
        if (messages[0] && typeof messages[0] === 'object' && messages[0].condition) {
            // Get the rulesModel to look up rule definitions
            const rulesModel = pageContext.getProps().models.rulesModel;
            const state = pageContext.getProps().store.getState();

            // Helper: recursively collect all leaf (non-compound) rules from a rule expression
            // Includes cycle guard to prevent infinite recursion on circular rule dependencies
            const collectLeafRules = (ruleExpr, visited = new Set()) => {
                const extracted = rulesModel.extractRules(ruleExpr);
                const leafRules = [];
                extracted.forEach((r) => {
                    if (r.type === 'CompoundRule') {
                        const compoundId = r.schema.id || r.schema.ruleExpr;
                        // Cycle guard: skip if we've already visited this compound rule
                        if (!visited.has(compoundId)) {
                            visited.add(compoundId);
                            leafRules.push(...collectLeafRules(r.schema.ruleExpr, visited));
                        }
                    } else {
                        leafRules.push(r);
                    }
                });
                return leafRules;
            };

            // Helper: build stateValues map (stateKey → value) required by evaluateRuleExpr,
            // handles static filterValues, dynamic filterValue.stateKey, and range min/max stateKeys
            const buildStateValues = (ruleExpr) => {
                const stateValues = {};
                collectLeafRules(ruleExpr).forEach((r) => {
                    if (!r.schema || !r.schema.stateKey) return;
                    stateValues[r.schema.stateKey] = state.getIn(r.schema.stateKey.split('.'));
                    if (r.schema.filterValue && r.schema.filterValue.stateKey) {
                        const fk = r.schema.filterValue.stateKey;
                        stateValues[fk] = state.getIn(fk.split('.'));
                    }
                    // Dynamically derive range property names from filterValue instead of hardcoding
                    if (r.schema.filterValue && typeof r.schema.filterValue === 'object') {
                        for (const rangeName of Object.keys(r.schema.filterValue)) {
                            const rangeFv = r.schema.filterValue[rangeName];
                            if (rangeFv && typeof rangeFv === 'object' && rangeFv.stateKey) {
                                stateValues[rangeFv.stateKey] = state.getIn(rangeFv.stateKey.split('.'));
                            }
                        }
                    }
                });
                return stateValues;
            };

            // Helper function to evaluate a single rule by identifier (base or compound)
            const evaluateRule = (ruleIdentifier) => {
                try {
                    const rule = rulesModel.rules[ruleIdentifier];
                    if (!rule) return false;

                    // Base / Dynamic rule — evaluate directly without going through rulesCache
                    if (rule.schema.stateKey && rule.type !== 'CompoundRule') {
                        const stateValue = state.getIn(rule.schema.stateKey.split('.'));
                        let expected = rule.schema.filterValue;
                        // Resolve dynamic filterValue.stateKey reference
                        if (expected && expected.stateKey) {
                            expected = state.getIn(expected.stateKey.split('.'));
                        }
                        // Resolve range min/max stateKey references dynamically
                        if ((rule.schema.filterOption === 'range' || rule.schema.filterOption === 'dateRange') && expected && typeof expected === 'object') {
                            expected = { ...expected };
                            for (const rangeName of Object.keys(expected)) {
                                if (expected[rangeName] && typeof expected[rangeName] === 'object' && expected[rangeName].stateKey) {
                                    expected[rangeName] = state.getIn(expected[rangeName].stateKey.split('.'));
                                }
                            }
                        }
                        return rule.evaluate(stateValue, expected);
                    }

                    // Compound rule — build stateValues and use evaluateRuleExpr.
                    // Use ruleIdentifier as the id so each compound rule gets its own cache slot.
                    if (rule.type === 'CompoundRule') {
                        const stateValues = buildStateValues(rule.schema.ruleExpr);
                        const props = rulesModel.evaluateRuleExpr(
                            { rule: ruleIdentifier, controls: '_eval_', id: ruleIdentifier },
                            stateValues
                        );
                        return props._eval_.result;
                    }

                    return false;
                } catch (error) {
                    return false;
                }
            };

            // Helper function to evaluate condition (supports AND/OR operators and single string format)
            const evaluateCondition = (condition) => {
                // Schema-based format: { operator: 'AND' | 'OR', rules: [...] }
                if (typeof condition === 'object' && condition.operator && Array.isArray(condition.rules)) {
                    const results = condition.rules.map((ruleIdentifier) => evaluateRule(ruleIdentifier));
                    if (condition.operator === 'AND') return results.every((result) => result === true);
                    if (condition.operator === 'OR')  return results.some((result) => result === true);
                    return false;
                }

                // Legacy string format: single rule identifier
                if (typeof condition === 'string') {
                    return evaluateRule(condition);
                }

                return false;
            };

            const activeEntry = messages.find((entry) => {
                try {
                    return evaluateCondition(entry.condition);
                } catch (error) {
                    return false;
                }
            });

            if (activeEntry) {
                return activeEntry.content;
            }
            return [];
        }

        // flat array of strings(Can be used as a fallback or for simple use cases that don't require conditional logic)
        return messages;
    }

    handleMessageTransition = () => {
        // Start fade out
        this.setState({ isFading: true });
        this.messageTransitionTimer = setTimeout(() => {
            // After fade out, change message and fade in
            this.setState({ messageIndex: 1, isFading: false });
        }, 500); // 500ms matches the CSS transition
    };

    goToNextPage() {
        const { schema, dispatch, selectedValues } = this.props;
        const { visualProps = {} } = get(schema, 'field', {});
        const { nextPageName } = visualProps;
        const pageSchemaModel = pageContext.getProps().models.pageSchemaModel;
        const progressionModel = pageContext.getProps().models.progressionModel;
        const pageName = pageContext.getProps().pageState.pageName;
        const apiSchema = pageSchemaModel.getApiSchemaByPageName(pageName);
        const interpolatedNextPageName = interpolateString(nextPageName)(selectedValues || {});
        apiSchema.redirectResponse = excludePageFromJourney(
            progressionModel,
            pageSchemaModel,
            interpolatedNextPageName,
            this.props
        );
        dispatch({
            type: 'VALIDATE_AND_PROGRESS',
            pageName,
            apiSchema,
            stateKey: 'vertical.quote',
            replaceHistory: true
        });
    }

    componentWillUnmount() {
        this.isMounted_ = false;
        // Prevent delayed actions after unmounting or memory leaks from timers
        if (this.messageTimer) {
            clearTimeout(this.messageTimer);
        }
        if (this.nextPageDelayTimer) {
            clearTimeout(this.nextPageDelayTimer);
            this.nextPageDelayTimer = null;
        }
        if (this.messageTransitionTimer) {
            clearTimeout(this.messageTransitionTimer);
        }
    }

    render() {
        const { schema, brandCode } = this.props;
        const { messageIndex, isFading } = this.state;
        const activeMessages = this.activeMessagesSnapshot || this.getActiveMessages();
        const currentMessage = activeMessages[messageIndex] || 'Please wait...';
        const spinnerType = brandCode === 'islt' ? 'Default' : 'CircularProgress';

        // UI Component class names with brand styling
        const buttonClasses = classNames(
            styles.loadingPage,
            brandCode && styles[brandCode.toLowerCase()]
        );

        return (
            <div className={classNames(
                buttonClasses,
                styles.heightContainer,
                brandCode && brandCode.toLowerCase()
            )}>
                <div className={classNames(
                    buttonClasses,
                    styles.container,
                    brandCode && brandCode.toLowerCase()
                )}>
                    <h2
                        className={classNames(
                            buttonClasses,
                            styles.fadeText,
                            brandCode && brandCode.toLowerCase()
                        )}
                        style={{ opacity: isFading ? 0 : 1, transition: 'opacity 0.5s ease', textAlign: 'center' }}
                    >
                        {currentMessage}
                    </h2>
                    <div className={classNames(
                        buttonClasses,
                        styles.spinner,
                        brandCode && brandCode.toLowerCase()
                    )}>
                        <LoadingSpinner type={spinnerType} />
                    </div>
                    <span>
                        {'If you are not automatically redirected to the next step within 30 seconds, please click '}
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                this.goToNextPage();
                            }}
                            className={classNames(
                                buttonClasses,
                                styles.link,
                                brandCode && brandCode.toLowerCase()
                            )}
                        >
                            here
                        </a>
                    </span>
                </div>
            </div>
        );
    }

}

_LoadingPage.propTypes = {
    schema: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    selectedValues: PropTypes.object,
    formData: PropTypes.object,
    brandCode: PropTypes.string,
    journeyId: PropTypes.string,
    serverConfig: PropTypes.object
};

/**
 * Map entire form data from state to props
 * @param {Immutable.Map} state - The application state
 * @returns {Object} - The form data and error state to be added to props
 */
const mapStateToProps = (state) => {
    const form = quoteFormSelector(state);
    // Convert Immutable Map to plain JavaScript object for API payload
    const formData = form ? form.toJS() : {};
    const brandCode = getBrand(state);
    const serverConfig = state.get('serverConfig');
    return {
        formData,
        brandCode,
        journeyId: state.getIn(['user', 'journey', 'transaction', 'journeyId']),
        serverConfig
    };
};

const LoadingPage = connect(mapStateToProps)(_LoadingPage);

export { _LoadingPage, LoadingPage };
